/* Module Definitions for the Alphabet.
   Single source of truth for the menu (/practice/alphabet) and exercise screens.
   Module 1: Content served by backend.
   Modules 2-4: Static content optimized for pattern recognition and quick memorization.
*/

const D = "◌"; // Generic dotted circle for diacritics

/* ── Module 2: Harakat (Short Vowels & Modifiers) ── */
const HARAKAT_GROUPS = [
  {
    id: "h-voyelles",
    get label() {
      return "Short Vowels";
    },
    letter_details: [
      {
        char: `${D}َ`,
        name: "Fatha",
        sound: "a",
        hint: "Dash ABOVE = 'a'. (بَ = ba)",
      },
      {
        char: `${D}ِ`,
        name: "Kasra",
        sound: "i",
        hint: "Dash BELOW = 'i'. (بِ = bi)",
      },
      {
        char: `${D}ُ`,
        name: "Damma",
        sound: "u / ou",
        hint: "Loop ABOVE = 'u'. (بُ = bu)",
      },
    ],
  },
  {
    id: "h-modificateurs",
    get label() {
      return "Modifiers";
    },
    letter_details: [
      {
        char: `${D}ْ`,
        name: "Soukoun",
        sound: "(no vowel)",
        hint: "Circle ABOVE = mute/dry consonant. No vowel added.",
      },
      {
        char: `${D}ّ`,
        name: "Chadda",
        sound: "double consonant",
        hint: "W-shape = double consonant. Hold the sound longer.",
      },
      {
        char: `${D}ً`,
        name: "Tanwine",
        sound: "an / in / un",
        hint: "Double vowel at word end = adds an 'n' sound.",
      },
    ],
  },
];

/* ── Module 3: Contextual Forms ── */
const form = (char, name, sound, hint) => ({ char, name, sound, hint });

const formEntry = (char, letterName, position, sound, hint) => ({
  char,
  name: `${letterName} — ${position}`,
  letterName,
  position,
  sound,
  hint,
});

const letterForms = (base, name, sound, hint) => [
  formEntry(`${base}ـ`, name, "initial", sound, hint),
  formEntry(`ـ${base}ـ`, name, "medial", sound, hint),
  formEntry(`ـ${base}`, name, "final", sound, hint),
];

const FAMILY = (id, label, letters) => ({
  id,
  label,
  letters: letters.map((l) => l[0]).join(" "),
  letter_details: letters.flatMap((l) => letterForms(...l)),
});

const FORMS_GROUPS = [
  FAMILY("f-ba", "Boat Family (Ba, Ta, Tha)", [
    ["ب", "Ba", "b", "1 dot below. Like a boat with a dropped anchor."],
    ["ت", "Ta", "t", "2 dots above. Looks like a smiley face."],
    ["ث", "Tha", "th (soft)", "3 dots above. Like 'th' in 'think'."],
  ]),
  FAMILY("f-jim", "Wave Family (Jim, Ha, Kha)", [
    ["ج", "Jim", "j", "1 dot inside/below. Like 'j' in 'jump'."],
    ["ح", "Ha", "h (deep)", "No dots. A sharp, deep 'h' from the chest."],
    ["خ", "Kha", "kh", "1 dot above. Raspy sound, like Spanish 'jota'."],
  ]),
  FAMILY("f-sin", "Teeth Family (Sin, Shin)", [
    ["س", "Sin", "s", "3 teeth, no dots."],
    ["ش", "Shin", "sh", "3 teeth + 3 dots above. Like 'sh' in 'shoe'."],
  ]),
  FAMILY("f-sad", "Loop Family (Sad, Dad)", [
    ["ص", "Sad", "s (heavy)", "Loop + tail, no dots. A deep, heavy 's'."],
    ["ض", "Dad", "d (heavy)", "Loop + tail + 1 dot. Unique to Arabic."],
  ]),
  FAMILY("f-ta2", "Stem Family (Ta, Za)", [
    ["ط", "Ta (heavy)", "t (heavy)", "Tall stem, no dots. Heavy 't'."],
    ["ظ", "Za (heavy)", "dh/z (heavy)", "Tall stem + 1 dot. Heavy 'th'/'z'."],
  ]),
  FAMILY("f-ayn", "Open Family (Ayn, Ghayn)", [
    ["ع", "Ayn", "ʿ (throat)", "No dots. Deep throat squeeze. Unique sound."],
    ["غ", "Ghayn", "gh", "1 dot above. Like gargling or French 'r'."],
  ]),
  FAMILY("f-fa", "Round Family (Fa, Qaf)", [
    ["ف", "Fa", "f", "1 dot above. Sits flat on the line."],
    ["ق", "Qaf", "q (deep)", "2 dots above. Dips below the line. Deep 'k'."],
  ]),
  FAMILY("f-kaf", "Kaf", [
    ["ك", "Kaf", "k", "Changes shape totally when connected (initial/medial)."],
  ]),
  FAMILY("f-lam", "Lam", [
    ["ل", "Lam", "l", "Tall stem, deep tail drop at the end."],
  ]),
  FAMILY("f-mim", "Mim", [
    ["م", "Mim", "m", "Small loop. Tail drops straight down at the end."],
  ]),
  FAMILY("f-nun", "Nun", [
    ["ن", "Nun", "n", "1 dot above. Deep bowl shape at the end."],
  ]),
  FAMILY("f-ha", "Ha", [
    [
      "ه",
      "Ha",
      "h (airy)",
      "Changes shape completely based on position: هـ ـهـ ـه ه.",
    ],
  ]),
  FAMILY("f-ya", "Ya", [
    ["ي", "Ya", "y / i", "2 dots below. Dots stay visible when attached."],
  ]),
  {
    id: "f-rebelles",
    get label() {
      return "The 6 Breakers (Non-Connectors)";
    },
    letters: "ا د ذ ر ز و",
    letter_details: [
      formEntry("ـا", "Alif", "attached", "ā", "Connects right, NEVER left."),
      formEntry("ـد", "Dal", "attached", "d", "Breaks the word connection."),
      formEntry(
        "ـذ",
        "Dhal",
        "attached",
        "dh",
        "1 dot above. Breaks the word.",
      ),
      formEntry("ـر", "Ra", "attached", "r", "Tail drops. Breaks the word."),
      formEntry("ـز", "Zay", "attached", "z", "1 dot above. Breaks the word."),
      formEntry(
        "ـو",
        "Waw",
        "attached",
        "w / ū",
        "Loop + tail. Breaks the word.",
      ),
    ],
  },
];

/* ── Module 4: Ligatures & Words ── */
const LIGATURES_GROUPS = [
  {
    id: "l-lamalif",
    get label() {
      return "Lam-Alif Ligature";
    },
    letter_details: [
      form(
        "لا",
        "Lam-Alif — isolated",
        "lā",
        "Mandatory: ل + ا always form this shape.",
      ),
      form(
        "ـلا",
        "Lam-Alif — attached",
        "lā",
        "Used when the previous letter connects (e.g., بلا).",
      ),
    ],
  },
  {
    id: "l-coupures",
    get label() {
      return "Breakers in Action";
    },
    letter_details: [
      form(
        "ا",
        "Alif",
        "ā",
        "Breaks the word. Next letter resets to initial shape.",
      ),
      form("د", "Dal", "d", "Breaks the word: مدرسة."),
      form("ذ", "Dhal", "dh", "Breaks the word: هذا."),
      form("ر", "Ra", "r", "Breaks the word: درس."),
      form("ز", "Zay", "z", "Breaks the word: وزير."),
      form("و", "Waw", "w / ū", "Breaks the word: ولد."),
    ],
  },
  {
    id: "l-mots",
    get label() {
      return "Simple Words";
    },
    letter_details: [
      form(
        "باب",
        "bāb",
        "door",
        "3 connected letters: بـ ـا then a break, then ب.",
      ),
      form("بيت", "bayt", "house", "Fully connected from start to finish."),
      form(
        "دار",
        "dār",
        "house",
        "Dal breaks it. Alif breaks it. 3 isolated letters.",
      ),
      form("ولد", "walad", "boy", "Waw breaks it, then Lam connects to Dal."),
      form("نور", "nūr", "light", "Nun connects to Waw. Waw breaks it."),
      form(
        "قلم",
        "qalam",
        "pen",
        "Fully connected. Mim tail drops at the end.",
      ),
      form("شمس", "shams", "sun", "3 teeth of Shin flowing into Mim into Sin."),
      form("يد", "yad", "hand", "Just two connected letters: يـ + ـد."),
    ],
  },
];

const count = (groups) =>
  groups.reduce((n, g) => n + (g.letter_details || []).length, 0);

export const ALPHABET_MODULES = [
  {
    slug: "lettres",
    n: 1,
    get title() {
      return "The 28 Letters";
    },
    get tagline() {
      return "The Foundation";
    },
    sample: "ا ب ت ث",
    get intro() {
      return "Learn the isolated shapes and sounds of the 28 Arabic letters.";
    },
    get why() {
      return "This is your baseline. You must recognize a letter's standalone form before seeing how it connects.";
    },
    total: 29,
    remote: true,
    modes: [
      ["both", "Name + Sound", "recommended"],
      ["name", "Name only", "ث → tha"],
      ["sound", "Sound only", "ث → th"],
    ],
  },
  {
    slug: "harakat",
    n: 2,
    get title() {
      return "Short Vowels (Harakat)";
    },
    get tagline() {
      return "Adding Sound";
    },
    sample: "◌َ ◌ِ ◌ُ",
    get intro() {
      return "Arabic letters are mostly consonants. Small symbols added above or below give them vowel sounds.";
    },
    get why() {
      return "Without Harakat, you're just looking at consonants. These symbols bring the words to life.";
    },
    groups: HARAKAT_GROUPS,
    total: count(HARAKAT_GROUPS),
    modes: [
      ["both", "Name + Sound", "recommended"],
      ["name", "Name only", "◌َ → fatha"],
      ["sound", "Sound only", "◌َ → a"],
    ],
  },
  {
    slug: "formes",
    n: 3,
    get title() {
      return "Connected Shapes";
    },
    get tagline() {
      return "Pattern Recognition";
    },
    sample: "بـ ـبـ ـب",
    get intro() {
      return "Arabic is written in cursive. Letters change shape depending on where they sit in a word.";
    },
    get why() {
      return "Focus on the dots and the core shapes. The tail often disappears when a letter connects.";
    },
    groups: FORMS_GROUPS,
    total: count(FORMS_GROUPS),
    modes: [
      ["both", "Shape + Position", "recommended"],
      ["name", "Letter Name", "ـبـ → ba"],
      ["sound", "Position", "ـبـ → medial"],
    ],
  },
  {
    slug: "ligatures",
    n: 4,
    get title() {
      return "Reading Words";
    },
    get tagline() {
      return "Putting it Together";
    },
    sample: "لا باب بيت",
    get intro() {
      return "Learn how letters interact in real words, including the 6 'Breakers' that refuse to connect to the left.";
    },
    get why() {
      return "This module bridges the gap between isolated rules and actual reading comprehension.";
    },
    groups: LIGATURES_GROUPS,
    total: count(LIGATURES_GROUPS),
    modes: [
      ["both", "Read + Meaning", "recommended"],
      ["name", "Reading", "باب → bāb"],
      ["sound", "Meaning", "باب → door"],
    ],
  },
];

export const getModule = (slug) =>
  ALPHABET_MODULES.find((m) => m.slug === slug) || ALPHABET_MODULES[0];

export const moduleChars = (m) =>
  (m.groups || []).flatMap((g) => (g.letter_details || []).map((l) => l.char));

export const staticChars = new Set(
  ALPHABET_MODULES.flatMap((m) => moduleChars(m)),
);

export const UNLOCK_PCT = 80;
